import {ReactNode, useEffect, useMemo, useState} from 'react'
import {Activity, Building2, ChevronLeft, ChevronRight, Database, FileSpreadsheet, LayoutDashboard, LoaderCircle, RotateCcw, Search, Users} from 'lucide-react'
import {Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts'
import * as XLSX from 'xlsx'
import './ContentAnalytics.scss'

export type ContentKind = 'dashboards' | 'reports'

const loadingMessages = [
  'Requisitando informações na base de dados',
  'Vinculando usuários e empresas',
  'Organizando dados do período selecionado',
  'Calculando indicadores e distribuições',
  'Preparando gráficos e detalhamentos',
]

export const ContentAnalyticsLoader = () => {
  const [activeMessage, setActiveMessage] = useState(0)

  useEffect(() => {
    const lastMessage = loadingMessages.length - 1
    if (activeMessage >= lastMessage) return
    const penultimateMessage = loadingMessages.length - 2
    const delay = activeMessage === penultimateMessage ? 1700 * 4 : 1700 * 3
    const timer = window.setTimeout(() => {
      setActiveMessage((current) => Math.min(current + 1, lastMessage))
    }, delay)
    return () => window.clearTimeout(timer)
  }, [activeMessage])

  return (
    <div className='ia-content-loader' role='status' aria-live='polite'>
      <div className='ia-content-loader__orb'><LoaderCircle size={32} /></div>
      <div className='ia-content-loader__eyebrow'>Inteligência em processamento</div>
      <h2>Construindo sua análise</h2>
      <p key={activeMessage}>{loadingMessages[activeMessage]}…</p>
      <div className='ia-content-loader__progress'><span key={`progress-${activeMessage}`} /></div>
    </div>
  )
}

type Props = {
  data: any
  kind: ContentKind
  onKindChange: (kind: ContentKind) => void
}

const colors = ['#3157d5', '#0e9f8a', '#e9a23b', '#7a58c7', '#dd5b69', '#4d91c7']
const number = (value: unknown) => new Intl.NumberFormat('pt-BR').format(Number(value || 0))
const dateTime = (value: unknown) => value
  ? new Intl.DateTimeFormat('pt-BR', {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'}).format(new Date(String(value)))
  : '—'

const itemName = (row: any, kind: ContentKind) => kind === 'dashboards'
  ? row.dashboard_name || row.log_name || 'Dashboard sem nome'
  : row.log_name || row.log_type_name || 'Relatório sem nome'

const dayKey = (value: unknown) => {
  const parsed = new Date(String(value || ''))
  return Number.isNaN(parsed.getTime()) ? 'Data desconhecida' : parsed.toISOString().slice(0, 10)
}

const summarize = (rows: any[], kind: ContentKind) => {
  const logs = new Set(rows.map((row) => row.id_log))
  const users = new Set(rows.map((row) => row.id_user).filter(Boolean))
  const companies = new Set(rows.map((row) => row.id_company).filter(Boolean))
  const items = new Set(rows.map((row) => itemName(row, kind)))
  const evolution = new Map<string, {bucket: string; logs: Set<any>; users: Set<any>}>()
  const topItems = new Map<string, {name: string; logs: Set<any>; users: Set<any>}>()
  const companyMap = new Map<string, {name: string; logs: Set<any>}>()

  rows.forEach((row) => {
    const day = dayKey(row.log_created_at)
    if (!evolution.has(day)) evolution.set(day, {bucket: day, logs: new Set(), users: new Set()})
    evolution.get(day)?.logs.add(row.id_log)
    evolution.get(day)?.users.add(row.id_user)

    const name = itemName(row, kind)
    if (!topItems.has(name)) topItems.set(name, {name, logs: new Set(), users: new Set()})
    topItems.get(name)?.logs.add(row.id_log)
    topItems.get(name)?.users.add(row.id_user)

    const company = row.company_name || 'Empresa não identificada'
    if (!companyMap.has(company)) companyMap.set(company, {name: company, logs: new Set()})
    companyMap.get(company)?.logs.add(row.id_log)
  })

  return {
    metrics: {accesses: logs.size, users: users.size, companies: companies.size, items: items.size},
    evolution: Array.from(evolution.values()).map((entry) => ({bucket: entry.bucket, accesses: entry.logs.size, users: entry.users.size})).sort((a, b) => a.bucket.localeCompare(b.bucket)),
    topItems: Array.from(topItems.values()).map((entry) => ({name: entry.name, accesses: entry.logs.size, users: entry.users.size})).sort((a, b) => b.accesses - a.accesses).slice(0, 12),
    companies: Array.from(companyMap.values()).map((entry) => ({name: entry.name, value: entry.logs.size})).sort((a, b) => b.value - a.value).slice(0, 10),
  }
}

const Card = ({label, value, icon: Icon, tone = 'blue'}: {label: string; value: number; icon: typeof Activity; tone?: string}) => (
  <div className={`ia-metric ia-content-metric ia-content-metric--${tone}`}>
    <div className='ia-metric__top'><span className='ia-metric__icon'><Icon size={16} /></span></div>
    <div className='ia-metric__label'>{label}</div>
    <div className='ia-metric__value'>{number(value)}</div>
  </div>
)

const Panel = ({title, subtitle, children}: {title: string; subtitle?: string; children: ReactNode}) => (
  <section className='ia-panel'>
    <div className='ia-panel__header'><div><h2 className='ia-panel__title'>{title}</h2>{subtitle ? <div className='ia-panel__subtitle'>{subtitle}</div> : null}</div></div>
    {children}
  </section>
)

const exportFiltered = (rows: any[], summary: ReturnType<typeof summarize>, kind: ContentKind) => {
  const workbook = XLSX.utils.book_new()
  const metrics = [
    {indicador: 'Acessos', valor: summary.metrics.accesses},
    {indicador: 'Usuários únicos', valor: summary.metrics.users},
    {indicador: 'Empresas', valor: summary.metrics.companies},
    {indicador: kind === 'dashboards' ? 'Dashboards' : 'Relatórios/ações', valor: summary.metrics.items},
  ]
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(metrics), 'Resumo')
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summary.evolution), 'Evolução')
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summary.topItems), kind === 'dashboards' ? 'Dashboards' : 'Relatórios')
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(rows.length ? rows : [{informacao: 'Sem dados'}]), 'Acessos')
  XLSX.writeFile(workbook, `analytics-${kind}-${new Date().toISOString().slice(0, 10)}.xlsx`)
}

const ContentAnalytics = ({data, kind, onKindChange}: Props) => {
  const [search, setSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState('')
  const [companyType, setCompanyType] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const rows = useMemo(() => data?.rows || [], [data])

  useEffect(() => {
    setSearch('')
    setSelectedItem('')
    setCompanyType('')
    setPage(1)
  }, [kind])

  const itemOptions = useMemo(() => Array.from(new Set<string>(rows.map((row: any) => itemName(row, kind)))).sort(), [rows, kind])
  const companyTypes = useMemo(() => Array.from(new Set<string>(rows.map((row: any) => row.company_type).filter(Boolean))).sort(), [rows])
  const filteredRows = useMemo(() => rows.filter((row: any) => {
    const haystack = `${itemName(row, kind)} ${row.log_type_name || ''} ${row.user_name || ''} ${row.email || ''} ${row.company_name || ''}`.toLowerCase()
    return (!search || haystack.includes(search.toLowerCase()))
      && (!selectedItem || itemName(row, kind) === selectedItem)
      && (!companyType || row.company_type === companyType)
  }), [rows, kind, search, selectedItem, companyType])
  const summary = useMemo(() => summarize(filteredRows, kind), [filteredRows, kind])
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const firstRow = filteredRows.length ? (currentPage - 1) * pageSize + 1 : 0
  const lastRow = Math.min(currentPage * pageSize, filteredRows.length)
  const pageRows = filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const pageNumbers = Array.from(new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages])).filter((value) => value >= 1 && value <= totalPages).sort((a, b) => a - b)
  const itemLabel = kind === 'dashboards' ? 'Dashboard' : 'Relatório/ação'

  useEffect(() => { setPage(1) }, [search, selectedItem, companyType, pageSize, rows.length])

  return (
    <div className='ia-content'>
      <div className='ia-content-switch' role='tablist' aria-label='Tipo de conteúdo analisado'>
        <button className={kind === 'dashboards' ? 'active' : ''} onClick={() => onKindChange('dashboards')}><LayoutDashboard size={15} /> Dashboards</button>
        <button className={kind === 'reports' ? 'active' : ''} onClick={() => onKindChange('reports')}><Database size={15} /> Relatórios</button>
      </div>

      <section className='ia-content-filters'>
        <div className='ia-content-search'><Search size={14} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder='Buscar usuário, empresa ou conteúdo' /></div>
        <select value={selectedItem} onChange={(event) => setSelectedItem(event.target.value)}><option value=''>Todos — {itemLabel}</option>{itemOptions.map((item) => <option key={item} value={item}>{item}</option>)}</select>
        <select value={companyType} onChange={(event) => setCompanyType(event.target.value)}><option value=''>Todos os tipos de empresa</option>{companyTypes.map((type) => <option key={type} value={type}>{type}</option>)}</select>
        <button className='ia-action' onClick={() => {setSearch(''); setSelectedItem(''); setCompanyType(''); setPage(1)}}><RotateCcw size={13} /> Limpar</button>
        <button className='ia-action ia-action--primary' onClick={() => exportFiltered(filteredRows, summary, kind)}><FileSpreadsheet size={13} /> Exportar visão</button>
      </section>

      {data?.truncated ? <div className='ia-content-warning'>A consulta atingiu o limite de {number(data.max_rows)} vínculos. Refine período, empresa ou usuário para uma análise integral.</div> : null}

      <div className='ia-metrics ia-content-metrics'>
        <Card label='Acessos registrados' value={summary.metrics.accesses} icon={Activity} />
        <Card label='Usuários únicos' value={summary.metrics.users} icon={Users} tone='green' />
        <Card label='Empresas alcançadas' value={summary.metrics.companies} icon={Building2} tone='purple' />
        <Card label={kind === 'dashboards' ? 'Dashboards acessados' : 'Relatórios e ações'} value={summary.metrics.items} icon={kind === 'dashboards' ? LayoutDashboard : Database} tone='amber' />
      </div>

      {!filteredRows.length ? <div className='ia-empty'><span className='ia-empty__icon'><Database size={22} /></span><h3>Nenhum acesso encontrado</h3><p>Ajuste os filtros ou amplie o período da consulta.</p></div> : <>
        <div className='ia-grid ia-grid--2'>
          <Panel title='Evolução dos acessos' subtitle='Acessos e usuários únicos por dia no recorte selecionado'>
            <div className='ia-chart'><ResponsiveContainer width='100%' height='100%'><AreaChart data={summary.evolution} margin={{top: 8, right: 8, left: -18, bottom: 0}}><defs><linearGradient id='contentAccessGradient' x1='0' y1='0' x2='0' y2='1'><stop offset='0%' stopColor='#3157d5' stopOpacity='.28' /><stop offset='100%' stopColor='#3157d5' stopOpacity='.02' /></linearGradient></defs><CartesianGrid vertical={false} strokeDasharray='3 3' /><XAxis dataKey='bucket' tickFormatter={(value) => new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR', {day: '2-digit', month: 'short'})} axisLine={false} tickLine={false} /><YAxis allowDecimals={false} axisLine={false} tickLine={false} /><Tooltip /><Legend iconType='circle' iconSize={7} /><Area type='monotone' dataKey='accesses' name='Acessos' stroke='#3157d5' fill='url(#contentAccessGradient)' strokeWidth={2} /><Area type='monotone' dataKey='users' name='Usuários' stroke='#0e9f8a' fill='transparent' strokeWidth={2} /></AreaChart></ResponsiveContainer></div>
          </Panel>
          <Panel title={`Top ${kind === 'dashboards' ? 'dashboards' : 'relatórios e ações'}`} subtitle='Conteúdos com maior número de acessos no período'>
            <div className='ia-chart'><ResponsiveContainer width='100%' height='100%'><BarChart data={summary.topItems.slice(0, 8)} layout='vertical' margin={{left: 40, right: 12}}><CartesianGrid horizontal={false} strokeDasharray='3 3' /><XAxis type='number' allowDecimals={false} axisLine={false} tickLine={false} /><YAxis type='category' dataKey='name' width={145} tickFormatter={(value) => String(value).slice(0, 24)} axisLine={false} tickLine={false} /><Tooltip /><Bar dataKey='accesses' name='Acessos' fill='#0e9f8a' radius={[0, 6, 6, 0]} maxBarSize={18} /></BarChart></ResponsiveContainer></div>
          </Panel>
        </div>
        <div className='ia-grid ia-grid--2'>
          <Panel title='Distribuição por empresa' subtitle='Participação das empresas nos acessos filtrados'>
            <div className='ia-chart ia-chart--compact'><ResponsiveContainer width='100%' height='100%'><PieChart><Pie data={summary.companies} dataKey='value' nameKey='name' innerRadius='50%' outerRadius='74%' paddingAngle={2} stroke='none'>{summary.companies.map((_, index) => <Cell key={index} fill={colors[index % colors.length]} />)}</Pie><Tooltip /><Legend iconType='circle' iconSize={7} /></PieChart></ResponsiveContainer></div>
          </Panel>
          <Panel title='Leitura do recorte' subtitle='O que está representado nesta consulta'>
            <div className='ia-content-insight'><strong>{number(summary.metrics.accesses)}</strong><span>acessos únicos em {number(filteredRows.length)} vínculos de empresa</span><p>Os logs identificam o usuário da ação. A empresa representa o vínculo atual encontrado em <code>user_company</code>.</p></div>
          </Panel>
        </div>

        <Panel title={kind === 'dashboards' ? 'Detalhamento de acessos a dashboards' : 'Detalhamento de acessos a relatórios'} subtitle={`${number(filteredRows.length)} vínculos encontrados · exibindo ${number(firstRow)}–${number(lastRow)}`}>
          <div className='ia-table-wrap'><table className='ia-table'><thead><tr><th>{itemLabel}</th>{kind === 'reports' ? <th>Tipo do log</th> : null}<th>Usuário</th><th>Empresa</th><th>Tipo de empresa</th><th>Data e hora</th></tr></thead><tbody>{pageRows.map((row: any, index: number) => <tr key={`${row.id_log}-${row.id_company}-${index}`}><td><strong>{itemName(row, kind)}</strong>{kind === 'dashboards' ? <span className='ia-table__path'>ID {row.id_dashboard}</span> : null}</td>{kind === 'reports' ? <td><span className='ia-method'>{row.log_type_name || `Tipo ${row.id_log_type}`}</span></td> : null}<td><div className='ia-user'><div className='ia-user__meta'><strong>{row.user_name || 'Usuário'}</strong><span>{row.email || '—'}</span></div></div></td><td>{row.company_name || '—'}</td><td>{row.company_type || '—'}</td><td>{dateTime(row.log_created_at)}</td></tr>)}</tbody></table></div>
          <div className='ia-pagination'>
            <div className='ia-pagination__size'><span>Itens por página</span><select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>{[10, 25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}</select></div>
            <div className='ia-pagination__summary'>{number(firstRow)}–{number(lastRow)} de {number(filteredRows.length)}</div>
            <div className='ia-pagination__pages'>
              <button aria-label='Página anterior' disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}><ChevronLeft size={14} /></button>
              {pageNumbers.map((pageNumber, index) => <span key={pageNumber} className='ia-pagination__number-group'>
                {index > 0 && pageNumber - pageNumbers[index - 1] > 1 ? <i>…</i> : null}
                <button className={pageNumber === currentPage ? 'active' : ''} aria-current={pageNumber === currentPage ? 'page' : undefined} onClick={() => setPage(pageNumber)}>{pageNumber}</button>
              </span>)}
              <button aria-label='Próxima página' disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}><ChevronRight size={14} /></button>
            </div>
          </div>
        </Panel>
      </>}
    </div>
  )
}

export default ContentAnalytics
